import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { AgentApiService } from 'src/app/core/services/agent-api.service';
import type { AgentRecord } from 'src/app/core/services/agent-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { TenantRow } from 'src/app/core/services/tenant-onboarding-api.service';
import {
  ApiKeyApiService,
  ApiKeyNeedingAttention,
  OwningTenant,
  WebhookEventOption,
  WebhookDelivery,
  WebhookHistoryEntry,
  WebhookTestResult,
  ApiKeyRecord,
  ApiScope,
  GeneratedApiKey,
  RegisteredWebhook,
  RotatedApiKey,
  WebhookRecord,
  WebhookDeadLetter,
} from 'src/app/core/services/api-key-api.service';
import type { PageSurfaceTab } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { PageBodyPanelComponent } from 'src/app/shared/page-body-panel/page-body-panel.component';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';

/**
 * How a client is named in a list, given the joined tenant may be absent or unnamed.
 *
 * A blank name counts as no name, not as a name: `tenant_name` is nullable text and an empty
 * string reaches the UI as readily as null, so falling back on nullish alone would render a row
 * with no owner at all. Written as an explicit emptiness check rather than a chain of `||`, so the
 * rule is visible instead of resting on which values happen to be falsy.
 */
function ownerLabelFor(
  tenant: { tenant_name: string | null; tenant_subdomain: string | null } | null,
  tenantId: number,
): string {
  const named = [tenant?.tenant_name, tenant?.tenant_subdomain].find(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );
  return named ?? `Client #${tenantId}`;
}

@Component({
  selector: 'app-manage-integrations',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, IconModule, PageSurfaceHeaderComponent, PageBodyPanelComponent, HasPermissionPipe, ActionMenuPanelDirective],
  templateUrl: './manage-integrations.component.html',
  styleUrl: './manage-integrations.component.css',
})
export class ManageIntegrationsComponent implements OnInit {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  /**
   * Which row's action menu is open, as `<table>-<id>`.
   *
   * One key across all three tables rather than one per table, so opening a menu anywhere closes
   * whatever was open — the same behaviour Manage Client has.
   */
  openActionMenuKey: string | null = null;

  private readonly api = inject(ApiKeyApiService);
  private readonly agentApi = inject(AgentApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);

  activeTab: 'api-keys' | 'webhooks' | 'dead-letters' = 'api-keys';

  readonly integrationSurfaceTabs: PageSurfaceTab[] = [
    { key: 'api-keys', label: 'API Keys' },
    { key: 'webhooks', label: 'Webhooks' },
    { key: 'dead-letters', label: 'Failed Deliveries' },
  ];

  // ── Agents for dropdowns ─────────────────────────────────────────────────

  // ── Clients + their agents for the API key form ──────────────────────────
  clients: TenantRow[] = [];
  keyAgents: AgentRecord[] = [];
  keyAgentsLoading = false;
  /** Agents of the client the webhook form is pointed at. Empty until one is chosen. */
  webhookAgents: AgentRecord[] = [];
  webhookAgentsLoading = false;

  // ── API Keys ─────────────────────────────────────────────────────────────
  /**
   * Filters for the key list.
   *
   * Sent to the server rather than applied to the page in hand: the list is paginated, so
   * filtering client-side would search only the rows already fetched and report a confident zero
   * for a key on page two.
   */
  syncingTenantId: number | null = null;
  syncNotice = '';

  /** Keys expiring soon or mid-rotation. Loaded once; small enough not to paginate. */
  attentionKeys: ApiKeyNeedingAttention[] = [];
  showAttention = false;

  keyFilters = { tenant_id: null as number | null, status: '', environment: '', q: '' };
  webhookFilters = { tenant_id: null as number | null, is_active: '', event: '' };

  apiKeys: ApiKeyRecord[] = [];
  apiKeysLoading = false;
  apiKeysError = '';
  apiKeysTotal = 0;

  showKeyModal = false;
  keyForm = {
    agent_id: null as number | null,
    tenant_id: null as number | null,
    label: '',
    expires_at: '',
    environment: 'live' as 'live' | 'test' | 'webhook',
    scopes: [] as string[],
    ip_whitelist: '',
    rate_limit_per_min: null as number | null,
  };
  availableScopes: ApiScope[] = [];
  customScope = '';
  keyGenLoading = false;
  keyGenError = '';
  newlyGeneratedKey: GeneratedApiKey | null = null;
  keyCopied = false;

  revokeLoading: Record<number, boolean> = {};

  showRotateModal = false;
  rotateTarget: ApiKeyRecord | null = null;
  rotateGraceHours = 48;
  rotateLoading = false;
  rotateError = '';
  rotatedKey: RotatedApiKey | null = null;
  rotatedKeyCopied = false;

  // ── Webhooks ─────────────────────────────────────────────────────────────
  webhooks: WebhookRecord[] = [];
  webhooksLoading = false;
  webhooksError = '';
  webhooksTotal = 0;

  supportedEvents: WebhookEventOption[] = [];
  showWebhookModal = false;
  webhookForm = {
    url: '',
    tenant_id: null as number | null,
    agent_id: null as number | null,
    label: '',
    events: [] as string[],
  };
  /** The webhook whose secret is being replaced, and the secret it produced. */
  rotateSecretTarget: WebhookRecord | null = null;
  rotateSecretLoading = false;
  rotateSecretError = '';
  rotatedWebhookSecret: string | null = null;
  rotatedSecretCopied = false;

  /** The webhook being probed, and what its endpoint said. */
  testingWebhookId: number | null = null;
  testEvent = 'pd.session.completed';
  testResult: WebhookTestResult | null = null;
  testError = '';

  /** The webhook whose delivery history is open, if any. */
  deliveriesFor: WebhookRecord | null = null;
  deliveries: WebhookHistoryEntry[] = [];
  deliveriesLoading = false;
  deliveriesError = '';
  deliveriesPage = 1;
  deliveriesPageSize = 25;
  deliveriesTotal = 0;
  deliveriesRetentionDays = 90;
  deliveryFilters = { outcome: '' as 'accepted' | 'failed' | '', event: '', is_test: '' as string };

  /** The webhook being edited, or null when the modal is registering a new one. */
  editingWebhookId: number | null = null;
  toggleWebhookLoading: Record<number, boolean> = {};
  webhookSaveLoading = false;
  webhookSaveError = '';
  newlyRegisteredWebhook: RegisteredWebhook | null = null;
  secretCopied = false;

  deleteWebhookLoading: Record<number, boolean> = {};

  // ── Dead-letter (failed webhook deliveries) ──────────────────────────────
  deadLetterClientId: number | null = null;
  /** Set when the queue was too large to scan in full, so the list below may be incomplete. */
  deadLettersTruncated = false;
  deadLetters: WebhookDeadLetter[] = [];
  deadLettersLoading = false;
  deadLettersError = '';
  deadLettersTotal = 0;
  replayLoading: Record<string, boolean> = {};
  discardLoading: Record<string, boolean> = {};

  ngOnInit(): void {
    this.loadApiKeys();
    this.loadWebhooks();
    this.loadDeadLetters();
    this.loadEvents();
    this.loadScopes();
    this.loadClients();
    this.loadAttention();
  }

  private loadAttention(): void {
    this.api
      .listKeysNeedingAttention(30)
      .subscribe({ next: (res) => (this.attentionKeys = res?.data?.result ?? []) });
  }

  /**
   * A lapsed rotation is the one to shout about: the client's old key has already stopped working,
   * so their integration is down now rather than at some point in the future.
   */
  get overdueCount(): number {
    return this.attentionKeys.filter((k) => k.reason === 'ROTATION_OVERDUE').length;
  }

  attentionLabel(key: ApiKeyNeedingAttention): string {
    if (key.reason === 'ROTATION_OVERDUE') return 'Rotation grace has lapsed — the old key no longer works';
    if (key.reason === 'ROTATING') {
      return `Rotating — the old key stops working in ${key.days_left ?? 0} day(s)`;
    }
    return key.days_left === 0 ? 'Expired' : `Expires in ${key.days_left} day(s)`;
  }

  ownerOf(key: ApiKeyNeedingAttention): string {
    return ownerLabelFor(key.tenant, key.tenant_id);
  }

  private loadScopes(): void {
    this.api.listScopes().subscribe({ next: (res) => (this.availableScopes = res?.data ?? []) });
  }

  private loadClients(): void {
    this.tenantApi
      .listTenants({ page: 1, page_size: 500 })
      .subscribe({ next: (res) => (this.clients = res?.data?.result ?? []) });
  }

  clearKeyFilters(): void {
    this.keyFilters = { tenant_id: null, status: '', environment: '', q: '' };
    this.loadApiKeys();
  }

  clearWebhookFilters(): void {
    this.webhookFilters = { tenant_id: null, is_active: '', event: '' };
    this.loadWebhooks();
  }

  get keyFiltersActive(): boolean {
    const f = this.keyFilters;
    // Compared explicitly rather than tested for truthiness: '' means "not filtered" here, and a
    // reader should not have to know which falsy values are possible to see that.
    return f.tenant_id !== null || f.status !== '' || f.environment !== '' || f.q !== '';
  }

  get webhookFiltersActive(): boolean {
    const f = this.webhookFilters;
    return f.tenant_id !== null || f.is_active !== '' || f.event !== '';
  }

  /** The client a row belongs to, falling back to the id when the join came back empty. */
  ownerLabel(row: { tenant: OwningTenant | null; tenant_id: number }): string {
    return ownerLabelFor(row.tenant, row.tenant_id);
  }

  clientLabel(t: TenantRow): string {
    const name = t.tenant_name ?? t.unique_id ?? `Client #${t.id}`;
    return t.tenant_name && t.unique_id ? `${name} — ${t.unique_id}` : name;
  }

  /**
   * Agents belonging to the client the webhook is for.
   *
   * A webhook scoped to an agent of a different client would subscribe to events that client's
   * agent never raises, so the endpoint would simply never fire and nothing would say why.
   */
  onWebhookClientChange(): void {
    this.webhookForm.agent_id = null;
    this.loadWebhookAgents();
  }

  private loadWebhookAgents(): void {
    this.webhookAgents = [];
    if (!this.webhookForm.tenant_id) return;
    this.webhookAgentsLoading = true;
    this.agentApi
      .listAgents({ tenant_id: this.webhookForm.tenant_id, tab: 'published', page: 1, page_size: 100 })
      .pipe(finalize(() => (this.webhookAgentsLoading = false)))
      .subscribe({ next: (res) => (this.webhookAgents = res?.data?.items ?? []) });
  }

  /** When the client changes, reset and reload that client's agents. */
  onKeyClientChange(): void {
    this.keyForm.agent_id = null;
    this.keyAgents = [];
    if (!this.keyForm.tenant_id) return;
    this.keyAgentsLoading = true;
    this.agentApi
      .listAgents({ tenant_id: this.keyForm.tenant_id, tab: 'published', page: 1, page_size: 100 })
      .pipe(finalize(() => (this.keyAgentsLoading = false)))
      .subscribe({ next: (res) => (this.keyAgents = res?.data?.items ?? []) });
  }

  switchTab(tab: 'api-keys' | 'webhooks' | 'dead-letters'): void {
    this.activeTab = tab;
  }

  onIntegrationTabKey(key: string): void {
    if (key === 'api-keys' || key === 'webhooks' || key === 'dead-letters') {
      this.switchTab(key);
    }
  }

  // ── Agents ───────────────────────────────────────────────────────────────

  agentLabel(a: AgentRecord): string {
    return a.agent_code ? `${a.agent_code} — ${a.name}` : a.name;
  }

  // ── API Keys ─────────────────────────────────────────────────────────────

  loadApiKeys(): void {
    this.apiKeysLoading = true;
    this.apiKeysError = '';
    this.api
      .listKeys({
        tenant_id: this.keyFilters.tenant_id ?? undefined,
        status: this.keyFilters.status || undefined,
        environment: this.keyFilters.environment || undefined,
        q: this.keyFilters.q || undefined,
      })
      .pipe(finalize(() => (this.apiKeysLoading = false)))
      .subscribe({
        next: (res) => {
          this.apiKeys = res?.data?.result ?? [];
          this.apiKeysTotal = res?.data?.total ?? 0;
        },
        error: () => (this.apiKeysError = 'Could not load API keys.'),
      });
  }

  openKeyModal(): void {
    this.keyForm = {
      agent_id: null,
      tenant_id: null,
      label: '',
      expires_at: '',
      environment: 'live',
      scopes: [],
      ip_whitelist: '',
      rate_limit_per_min: null,
    };
    this.customScope = '';
    this.keyAgents = [];
    this.keyGenError = '';
    this.newlyGeneratedKey = null;
    this.keyCopied = false;
    this.showKeyModal = true;
  }

  closeKeyModal(): void {
    this.showKeyModal = false;
    this.newlyGeneratedKey = null;
    if (this.keyCopied) this.loadApiKeys();
  }

  generateKey(): void {
    if (!this.keyForm.tenant_id) {
      this.keyGenError = 'Client is required.';
      return;
    }
    this.keyGenLoading = true;
    this.keyGenError = '';
    const ipWhitelist = this.parseList(this.keyForm.ip_whitelist);
    this.api
      .generateKey({
        agent_id: this.keyForm.agent_id ?? undefined,
        tenant_id: this.keyForm.tenant_id,
        label: this.keyForm.label || undefined,
        expires_at: this.keyForm.expires_at || undefined,
        environment: this.keyForm.environment,
        scopes: this.keyForm.scopes.length ? this.keyForm.scopes : undefined,
        ip_whitelist: ipWhitelist.length ? ipWhitelist : undefined,
        rate_limit_per_min: this.keyForm.rate_limit_per_min ?? undefined,
      })
      .pipe(finalize(() => (this.keyGenLoading = false)))
      .subscribe({
        next: (res) => {
          this.newlyGeneratedKey = res.data;
          this.loadAttention();
          this.loadApiKeys();
        },
        error: () => (this.keyGenError = 'Failed to generate API key.'),
      });
  }

  isScopeSelected(value: string): boolean {
    return this.keyForm.scopes.includes(value);
  }

  toggleScope(value: string): void {
    const idx = this.keyForm.scopes.indexOf(value);
    if (idx >= 0) this.keyForm.scopes.splice(idx, 1);
    else this.keyForm.scopes.push(value);
  }

  addCustomScope(): void {
    const value = this.customScope.trim();
    if (value && !this.keyForm.scopes.includes(value)) {
      this.keyForm.scopes.push(value);
    }
    this.customScope = '';
  }

  /** Selected scopes that are not part of the suggested catalog. */
  get customSelectedScopes(): string[] {
    const known = new Set(this.availableScopes.map((s) => s.value));
    return this.keyForm.scopes.filter((s) => !known.has(s));
  }

  /** Split a comma/newline-separated string into a trimmed, de-duped list. */
  private parseList(raw: string): string[] {
    const items = raw
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(items));
  }

  copyKey(): void {
    if (!this.newlyGeneratedKey) return;
    navigator.clipboard.writeText(this.newlyGeneratedKey.key).then(() => {
      this.keyCopied = true;
    });
  }

  revokeKey(id: number): void {
    this.revokeLoading[id] = true;
    this.api
      .revokeKey(id)
      .pipe(finalize(() => delete this.revokeLoading[id]))
      .subscribe({
        next: () => {
          this.loadApiKeys();
          // Revoking clears whatever put the key on the attention list.
          this.loadAttention();
        },
        error: () => {},
      });
  }

  openRotateModal(k: ApiKeyRecord): void {
    this.rotateTarget = k;
    this.rotateGraceHours = 48;
    this.rotateError = '';
    this.rotatedKey = null;
    this.rotatedKeyCopied = false;
    this.showRotateModal = true;
  }

  closeRotateModal(): void {
    this.showRotateModal = false;
    this.rotateTarget = null;
    this.rotatedKey = null;
  }

  rotateKey(): void {
    if (!this.rotateTarget) return;
    this.rotateLoading = true;
    this.rotateError = '';
    this.api
      .rotateKey(this.rotateTarget.id, { grace_hours: this.rotateGraceHours })
      .pipe(finalize(() => (this.rotateLoading = false)))
      .subscribe({
        next: (res) => {
          this.rotatedKey = res.data;
          this.loadApiKeys();
          this.loadAttention();
        },
        error: (err) =>
          (this.rotateError =
            resolveApiErrorMessage(err) || 'Failed to rotate API key.'),
      });
  }

  copyRotatedKey(): void {
    if (!this.rotatedKey) return;
    navigator.clipboard.writeText(this.rotatedKey.key).then(() => {
      this.rotatedKeyCopied = true;
    });
  }

  /** Badge label + classes for a key's lifecycle status (falls back to is_active for old records). */
  keyStatusMeta(k: ApiKeyRecord): { label: string; class: string } {
    const status = k.status ?? (k.is_active ? 'ACTIVE' : 'REVOKED');
    switch (status) {
      case 'ROTATING':
        return { label: 'Rotating', class: 'bg-warning/20 text-warning' };
      case 'REVOKED':
        return { label: 'Revoked', class: 'bg-danger/20 text-danger' };
      case 'EXPIRED':
        return { label: 'Expired', class: 'bg-gray-200 text-gray-500 dark:bg-white/10' };
      default:
        return { label: 'Active', class: 'bg-success/20 text-success' };
    }
  }

  /** A key can be rotated/revoked while it is live or within its rotation grace window. */
  canManageKey(k: ApiKeyRecord): boolean {
    const status = k.status ?? (k.is_active ? 'ACTIVE' : 'REVOKED');
    return status === 'ACTIVE' || status === 'ROTATING';
  }

  // ── Webhooks ─────────────────────────────────────────────────────────────

  loadWebhooks(): void {
    this.webhooksLoading = true;
    this.webhooksError = '';
    this.api
      .listWebhooks({
        tenant_id: this.webhookFilters.tenant_id ?? undefined,
        is_active: this.webhookFilters.is_active === '' ? undefined : this.webhookFilters.is_active === 'true',
        event: this.webhookFilters.event || undefined,
      })
      .pipe(finalize(() => (this.webhooksLoading = false)))
      .subscribe({
        next: (res) => {
          this.webhooks = res?.data?.result ?? [];
          this.webhooksTotal = res?.data?.total ?? 0;
        },
        error: () => (this.webhooksError = 'Could not load webhooks.'),
      });
  }

  private loadEvents(): void {
    this.api.listWebhookEvents().subscribe({ next: (res) => (this.supportedEvents = res?.data ?? []) });
  }

  openWebhookModal(): void {
    this.editingWebhookId = null;
    this.webhookForm = { url: '', tenant_id: null, agent_id: null, label: '', events: [] };
    this.webhookAgents = [];
    this.webhookSaveError = '';
    this.newlyRegisteredWebhook = null;
    this.secretCopied = false;
    this.showWebhookModal = true;
  }

  /**
   * Same modal, seeded from the row.
   *
   * The client is fixed while editing: moving an endpoint to another tenant would silently
   * redirect one client's events to another, so that is a new registration, not an edit.
   */
  openWebhookEditModal(webhook: WebhookRecord): void {
    this.editingWebhookId = webhook.id;
    this.webhookForm = {
      url: webhook.url,
      tenant_id: webhook.tenant_id,
      agent_id: webhook.agent_id,
      label: webhook.label ?? '',
      events: [...webhook.events],
    };
    this.webhookSaveError = '';
    this.newlyRegisteredWebhook = null;
    this.secretCopied = false;
    this.loadWebhookAgents();
    this.showWebhookModal = true;
  }

  closeWebhookModal(): void {
    this.showWebhookModal = false;
    this.editingWebhookId = null;
    this.newlyRegisteredWebhook = null;
    if (this.secretCopied) this.loadWebhooks();
  }

  /** One button for both modes, so the modal's primary action always says what it will do. */
  saveWebhook(): void {
    if (this.editingWebhookId === null) this.registerWebhook();
    else this.updateWebhook();
  }

  private updateWebhook(): void {
    const id = this.editingWebhookId;
    if (id === null) return;
    if (!this.webhookForm.url) {
      this.webhookSaveError = 'URL is required.';
      return;
    }
    this.webhookSaveLoading = true;
    this.webhookSaveError = '';
    this.api
      .updateWebhook(id, {
        url: this.webhookForm.url,
        agent_id: this.webhookForm.agent_id,
        label: this.webhookForm.label || undefined,
        events: this.webhookForm.events,
      })
      .pipe(finalize(() => (this.webhookSaveLoading = false)))
      .subscribe({
        next: () => {
          this.showWebhookModal = false;
          this.editingWebhookId = null;
          this.loadWebhooks();
        },
        error: () => (this.webhookSaveError = 'Failed to update webhook.'),
      });
  }

  /**
   * Probe the endpoint and show the answer.
   *
   * Defaults to the first event the webhook subscribes to, since that is the one whose shape the
   * client has actually written code for; a catch-all endpoint gets the completed-session event.
   */
  sendTestEvent(webhook: WebhookRecord): void {
    this.testingWebhookId = webhook.id;
    this.testResult = null;
    this.testError = '';
    const event = webhook.events[0] ?? 'pd.session.completed';
    this.api
      .testWebhook(webhook.id, event)
      .pipe(finalize(() => (this.testingWebhookId = null)))
      .subscribe({
        next: (res) => {
          this.testResult = res.data;
          // The probe is itself a delivery, so an open history is now one row out of date.
          if (this.deliveriesFor?.id === webhook.id) this.loadDeliveries();
          this.loadWebhooks();
        },
        error: () => (this.testError = 'Could not send the test event.'),
      });
  }

  askRotateSecret(webhook: WebhookRecord): void {
    this.rotateSecretTarget = webhook;
    this.rotateSecretError = '';
    this.rotatedWebhookSecret = null;
    this.rotatedSecretCopied = false;
  }

  closeRotateSecret(): void {
    this.rotateSecretTarget = null;
    this.rotatedWebhookSecret = null;
  }

  confirmRotateSecret(): void {
    const webhook = this.rotateSecretTarget;
    if (!webhook) return;
    this.rotateSecretLoading = true;
    this.rotateSecretError = '';
    this.api
      .rotateWebhookSecret(webhook.id)
      .pipe(finalize(() => (this.rotateSecretLoading = false)))
      .subscribe({
        next: (res) => {
          this.rotatedWebhookSecret = res.data.secret;
          // The rotation is itself a history entry, so an open history is now out of date.
          if (this.deliveriesFor?.id === webhook.id) this.loadDeliveries();
        },
        error: (err) => {
          this.rotateSecretError =
            err?.status === 403
              ? 'You do not have permission to rotate a webhook secret.'
              : 'Could not rotate the secret.';
        },
      });
  }

  copyRotatedSecret(): void {
    if (!this.rotatedWebhookSecret) return;
    navigator.clipboard.writeText(this.rotatedWebhookSecret).then(() => {
      this.rotatedSecretCopied = true;
    });
  }

  /** Narrowing for the template, which renders the two kinds of history row differently. */
  asDelivery(entry: WebhookHistoryEntry): WebhookDelivery | null {
    return entry.kind === 'DELIVERY' ? entry : null;
  }

  auditLabel(action: string): string {
    return action === 'secret_rotated'
      ? 'Signing secret rotated — deliveries signed before this used the previous secret'
      : action;
  }

  dismissTestResult(): void {
    this.testResult = null;
    this.testError = '';
  }

  openDeliveries(webhook: WebhookRecord): void {
    this.deliveriesFor = webhook;
    this.deliveriesPage = 1;
    this.deliveryFilters = { outcome: '', event: '', is_test: '' };
    this.loadDeliveries();
  }

  /** Re-reads the current page. Every filter goes to the server, so the total stays honest. */
  loadDeliveries(): void {
    const webhook = this.deliveriesFor;
    if (!webhook) return;
    this.deliveriesLoading = true;
    this.deliveriesError = '';
    this.api
      .listWebhookDeliveries(webhook.id, {
        page: this.deliveriesPage,
        page_size: this.deliveriesPageSize,
        outcome: this.deliveryFilters.outcome || undefined,
        event: this.deliveryFilters.event || undefined,
        is_test: this.deliveryFilters.is_test === '' ? undefined : this.deliveryFilters.is_test === 'true',
      })
      .pipe(finalize(() => (this.deliveriesLoading = false)))
      .subscribe({
        next: (res) => {
          this.deliveries = res?.data?.result ?? [];
          this.deliveriesTotal = res?.data?.total ?? 0;
          this.deliveriesRetentionDays = res?.data?.retention_days ?? 90;
        },
        error: () => (this.deliveriesError = 'Could not load delivery history.'),
      });
  }

  onDeliveryFiltersChange(): void {
    // Page 4 of the unfiltered history is usually past the end of the filtered one.
    this.deliveriesPage = 1;
    this.loadDeliveries();
  }

  clearDeliveryFilters(): void {
    this.deliveryFilters = { outcome: '', event: '', is_test: '' };
    this.onDeliveryFiltersChange();
  }

  get deliveryFiltersActive(): boolean {
    const f = this.deliveryFilters;
    return !!(f.outcome || f.event || f.is_test);
  }

  get deliveriesLastPage(): number {
    return Math.max(1, Math.ceil(this.deliveriesTotal / this.deliveriesPageSize));
  }

  goToDeliveriesPage(next: number): void {
    if (next < 1 || next > this.deliveriesLastPage || next === this.deliveriesPage) return;
    this.deliveriesPage = next;
    this.loadDeliveries();
  }

  closeDeliveries(): void {
    this.deliveriesFor = null;
  }

  /** What the endpoint answered, or why nothing did. */
  deliveryOutcome(d: WebhookDelivery): string {
    if (d.status_code !== null) return String(d.status_code);
    return d.error ? 'No response' : '—';
  }

  /** Pause or resume delivery without deleting the endpoint and re-issuing its secret. */
  toggleWebhookActive(webhook: WebhookRecord): void {
    this.toggleWebhookLoading[webhook.id] = true;
    this.api
      .updateWebhook(webhook.id, { is_active: !webhook.is_active })
      .pipe(finalize(() => (this.toggleWebhookLoading[webhook.id] = false)))
      .subscribe({
        next: () => this.loadWebhooks(),
        error: () => (this.webhooksError = 'Could not change the webhook state.'),
      });
  }

  toggleEvent(event: string): void {
    const idx = this.webhookForm.events.indexOf(event);
    if (idx >= 0) this.webhookForm.events.splice(idx, 1);
    else this.webhookForm.events.push(event);
  }

  isEventSelected(event: string): boolean {
    return this.webhookForm.events.includes(event);
  }

  registerWebhook(): void {
    if (!this.webhookForm.url || !this.webhookForm.tenant_id) {
      this.webhookSaveError = 'URL and Client are required.';
      return;
    }
    this.webhookSaveLoading = true;
    this.webhookSaveError = '';
    this.api
      .registerWebhook({
        url: this.webhookForm.url,
        tenant_id: this.webhookForm.tenant_id,
        agent_id: this.webhookForm.agent_id ?? undefined,
        label: this.webhookForm.label || undefined,
        events: this.webhookForm.events,
      })
      .pipe(finalize(() => (this.webhookSaveLoading = false)))
      .subscribe({
        next: (res) => {
          this.newlyRegisteredWebhook = res.data;
          this.loadWebhooks();
        },
        error: () => (this.webhookSaveError = 'Failed to register webhook.'),
      });
  }

  copySecret(): void {
    if (!this.newlyRegisteredWebhook) return;
    navigator.clipboard.writeText(this.newlyRegisteredWebhook.secret).then(() => {
      this.secretCopied = true;
    });
  }

  deleteWebhook(id: number): void {
    this.deleteWebhookLoading[id] = true;
    this.api
      .deleteWebhook(id)
      .pipe(finalize(() => delete this.deleteWebhookLoading[id]))
      .subscribe({ next: () => this.loadWebhooks(), error: () => {} });
  }

  // ── Dead-letter (failed webhook deliveries) ──────────────────────────────
  loadDeadLetters(): void {
    this.deadLettersLoading = true;
    this.deadLettersError = '';
    this.api
      .listWebhookDeadLetters({
        tenant_id: this.deadLetterClientId ?? undefined,
        page: 1,
        page_size: 100,
      })
      .pipe(finalize(() => (this.deadLettersLoading = false)))
      .subscribe({
        next: (res) => {
          this.deadLetters = res?.data?.result ?? [];
          this.deadLettersTotal = res?.data?.total ?? 0;
          this.deadLettersTruncated = res?.data?.truncated ?? false;
        },
        error: () => (this.deadLettersError = 'Could not load failed deliveries.'),
      });
  }

  /**
   * Copy a client's keys into that client's own database.
   *
   * Lifecycle actions push each key across as they happen; this is the repair pass for keys that
   * predate the mirror or whose push failed — the push is best-effort by design, so that it can
   * never block an operator's revoke.
   */
  syncKeysToTenant(tenantId: number): void {
    this.syncingTenantId = tenantId;
    this.syncNotice = '';
    this.apiKeysError = '';
    this.api
      .syncKeysToTenant(tenantId)
      .pipe(finalize(() => (this.syncingTenantId = null)))
      .subscribe({
        next: (res) => {
          const synced = res?.data?.synced ?? 0;
          this.syncNotice = `${synced} ${synced === 1 ? 'key' : 'keys'} copied into this client's database.`;
        },
        error: () => (this.apiKeysError = "Could not sync this client's keys."),
      });
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.agent-action-menu')) return;
    this.openActionMenuKey = null;
  }

  clearDeadLetterFilters(): void {
    this.deadLetterClientId = null;
    this.loadDeadLetters();
  }

  replayDeadLetter(jobId: string): void {
    this.replayLoading[jobId] = true;
    this.api
      .replayWebhookDeadLetter(jobId)
      .pipe(finalize(() => delete this.replayLoading[jobId]))
      .subscribe({ next: () => this.loadDeadLetters(), error: () => this.loadDeadLetters() });
  }

  discardDeadLetter(jobId: string): void {
    this.discardLoading[jobId] = true;
    this.api
      .discardWebhookDeadLetter(jobId)
      .pipe(finalize(() => delete this.discardLoading[jobId]))
      .subscribe({ next: () => this.loadDeadLetters(), error: () => this.loadDeadLetters() });
  }
}
